/**
 * Marketing API endpoints (contact, signup, teaser report, turnstile).
 * These endpoints are used by the marketing pages and the signup wizard.
 * Most are protected with a Cloudflare Turnstile token instead of a JWT.
 */

// Re-use the shared package's internal infrastructure.
// The parent index.ts will wire these up via re-exports.
import {
  buildApiUrl,
  buildNonScopedApiUrl,
  openStreamEventSource,
  type AsoEngineSseInitEvent,
  type AsoEngineSseUpdateEvent,
  type AsoEngineSseCompleteEvent,
} from "./index.js";
import type {
  AuditEmailFrequency,
  EmailCadenceApplyResponse,
  EmailPreferencesGetResponse,
  EmailPreferencesUpdateRequest,
  EmailPreferencesUpdateResponse,
  NewsletterConfirmRequest,
  NewsletterConfirmResponse,
  NewsletterSubscribeRequest,
  NewsletterSubscribeResponse,
  PublicKeywordCountResponse,
  PublicPlatformCountsResponse,
  ReleaseNotesResponse,
} from "@a7y/types";
import type { AsoEngineChildStatus } from "@a7y/audit-domain";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type MarketingApiResponse<TData = unknown> = {
  success: boolean;
  statusCode?: number;
  statusText?: string;
  data?: TData;
  error?: unknown;
};

type MarketingApiRequestBody = Record<string, unknown> | null;

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

async function marketingRequest<TData = unknown>(
  endpoint: string,
  method: "GET" | "POST",
  token?: string | null,
  body: MarketingApiRequestBody = null,
  queryParams?: Record<string, string | number | null | undefined> | null,
): Promise<MarketingApiResponse<TData>> {
  const url = buildApiUrl(endpoint, queryParams);
  const headers: Record<string, string> = {};
  const request: RequestInit = { method, headers };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  if (body) {
    request.body = JSON.stringify(body);
    headers["Content-Type"] = "application/json";
  }

  const res = await fetch(url, request);

  if (!res.ok) {
    const errorBody = await res.json().catch(() => ({}));
    return {
      success: false,
      statusText: res.statusText,
      statusCode: res.status,
      error: errorBody,
    };
  }

  const data = (await res.json()) as TData;
  return { success: true, statusCode: res.status, data };
}

// ---------------------------------------------------------------------------
// Contact
// ---------------------------------------------------------------------------

export async function apiSubmitContactForm(
  requestData: MarketingApiRequestBody,
): Promise<MarketingApiResponse> {
  return marketingRequest("contact/web-form", "POST", null, requestData);
}

export async function apiSubmitTeaserReport(
  requestData: MarketingApiRequestBody,
): Promise<MarketingApiResponse<{ submitted: boolean }>> {
  return marketingRequest<{ submitted: boolean }>("aso/teaser-report", "POST", null, requestData);
}

// ---------------------------------------------------------------------------
// QR-to-Audit /scan (A7Y-871)
// ---------------------------------------------------------------------------

export type ScanKickoffResponse = {
  success: boolean;
  auditId?: string;
  /** A7Y-2554: always "running" on a successful kickoff — the engine run is
   * dispatched, not finished, when this response lands. */
  status?: "running";
  /** "rate_limited" (429) or "daily_ceiling" (503) when blocked by A7Y-886 limits. */
  error?: string;
  /** Human-friendly copy when blocked by rate limits — surface to the user verbatim. */
  message?: string;
};

/**
 * Kicks off an anonymous quick audit from the /scan flow. No auth required —
 * the API route is in the preflight bypass list.
 *
 * A7Y-2554: returns as soon as the engine run has been dispatched, NOT when
 * the audit completes — a quick audit routinely runs past the ~100s
 * Cloudflare Worker subrequest ceiling. The caller gets `auditId` +
 * `status: "running"` immediately and polls `/scan/r/{auditId}` for the
 * result, tolerating 404s until the engine writes the audit doc.
 */
export async function apiKickoffScanAudit(
  body: { appListingUrl: string; source?: string; visitorCountry?: string },
): Promise<MarketingApiResponse<ScanKickoffResponse>> {
  return marketingRequest<ScanKickoffResponse>("aso/scan", "POST", null, body);
}

export type ScanClaimResponse =
  | {
      success: true;
      auditId: string;
      /** A7Y-1114 / A7Y-3630: the first-class project the claim created.
       * Undefined when the prepare step failed. */
      projectId?: string;
      /** A7Y-3630: the org that owns `projectId`. Present iff `projectId` is. */
      orgId?: string;
      /**
       * A7Y-5398: the other store the /start visitor chose, attached after the
       * project was created. Absent when none was sent or on a repeat claim.
       */
      secondStore?: { added: boolean; error?: string };
    }
  | { success: false; reason?: string; error?: string };

/**
 * A7Y-5398: what the /start wizard gathered while the audit ran, applied to
 * the project the claim creates. Every field is optional.
 */
export interface ScanClaimProjectOverrides {
  /** Replaces the listing's title as the project name (trimmed, 1–80 chars). */
  name?: string;
  /** The audit cadence; the API clamps it to the plan. */
  auditScheduleFrequency?: "daily" | "weekly" | "biweekly" | "monthly";
  /** The other store's app, added through add-app-entry. */
  secondStore?: { storeId: "appstore" | "playstore"; appId: string; regionId?: string };
}

/**
 * A7Y-885: typeahead app search for the /scan flow. Returns up to ~5
 * results per store. The frontend debounces and renders a picker; the
 * chosen result resolves to its store URL which then feeds the regular
 * audit kickoff via apiKickoffScanAudit.
 */
export type ScanSearchResultItem = {
  appId: string;
  title: string;
  subtitle?: string;
  storeUrl?: string;
  iconUrl?: string;
  developerName?: string;
};

export type ScanSearchResponse = {
  success: boolean;
  results?: {
    appstore: ScanSearchResultItem[];
    playstore: ScanSearchResultItem[];
  };
  error?: string;
  message?: string;
};

export async function apiScanSearch(
  params: { term: string; country?: string; limit?: number },
): Promise<MarketingApiResponse<ScanSearchResponse>> {
  return marketingRequest<ScanSearchResponse>(
    "aso/scan/search",
    "GET",
    null,
    null,
    {
      term: params.term,
      country: params.country ?? null,
      limit: params.limit ?? null,
    },
  );
}

/**
 * Claims an anonymous audit for the authenticated user. Pass the Clerk
 * session token; the API verifies the claim_token + token-holder pair and
 * atomically transfers ownership.
 */
export async function apiClaimScanAudit(
  token: string,
  body: { auditId: string; claimToken: string; project?: ScanClaimProjectOverrides },
): Promise<MarketingApiResponse<ScanClaimResponse>> {
  return marketingRequest<ScanClaimResponse>("aso/scan/claim", "POST", token, body);
}

// ---------------------------------------------------------------------------
// Affiliate referral offer (A7Y-2814)
// ---------------------------------------------------------------------------

export type AffiliateOfferResponseData = { discountPercent: number | null };

/**
 * Resolve a `?via=` referral token to the customer-facing discount its
 * affiliate grants, so the offer can be stated before Stripe checkout.
 *
 * Public route — no Clerk JWT, no Turnstile. Always 200 with exactly one key:
 * `discountPercent` is null for an unknown token, an inactive affiliate, and
 * an affiliate with no discount, which are deliberately indistinguishable.
 * Never call this before marketing consent — the edge worker covers the
 * pre-consent case by injecting the answer into the served HTML.
 */
export async function apiGetAffiliateOffer(
  token: string,
): Promise<MarketingApiResponse<AffiliateOfferResponseData>> {
  return marketingRequest<AffiliateOfferResponseData>(
    "affiliate/offer",
    "GET",
    null,
    null,
    { token },
  );
}

// ---------------------------------------------------------------------------
// /check-my-app — unlisted lead-capture audit (A7Y-921)
// ---------------------------------------------------------------------------

export type CheckMyAppSubmitRequest = {
  appUrlOrId: string;
  leadName?: string;
  leadEmail?: string;
  src?: string;
  /** Storefront country when the input carries none (A7Y-5396: a typeahead pick's region). */
  visitorCountry?: string;
  turnstileToken: string;
};

export type CheckMyAppSubmitErrorCode =
  | "unparseable_input"
  | "turnstile_failed"
  | "daily_cap"
  | "ip_daily_cap"
  | string;

export type CheckMyAppSubmitResponse =
  | { success: true; token: string; auditId: string; reused?: boolean }
  | { success: false; error: CheckMyAppSubmitErrorCode };

/**
 * Submit the /check-my-app form. Server-side flow:
 *   1. Verify + consume the Turnstile token.
 *   2. Parse the app URL or bare ID. 400 → `error: "unparseable_input"`.
 *   3. Check the global daily cap. 503 → `error: "daily_cap"`.
 *   4. Dedup against recent lead audits (24h window). Hit → returns the
 *      existing token + auditId with `reused: true`.
 *   5. Otherwise spawn a new lead audit and return the seeded token + auditId.
 *
 * Public route — no Clerk JWT. Turnstile token is the auth credential.
 */
export async function apiPostCheckMyAppSubmit(
  request: CheckMyAppSubmitRequest,
): Promise<MarketingApiResponse<CheckMyAppSubmitResponse>> {
  const { turnstileToken, ...body } = request;
  return marketingRequest<CheckMyAppSubmitResponse>(
    "aso/check-my-app/submit",
    "POST",
    turnstileToken,
    body as MarketingApiRequestBody,
  );
}

/**
 * After A7Y-1180 (V1 envelope rip-out, Phase D) the result page fetches the
 * V2 envelope itself via /aso/listing-audit/public-meta + signed-URL body.
 * The polling endpoint only returns the audit id so the page can dispatch
 * that fetch.
 *
 * A7Y-5397: `engines` is each engine's state for the /start run view —
 * queued/running/done, its live status line, and once done its score and
 * top finding (`headline`); `startedAt` is when the audit was submitted.
 * Empty for a run that recorded no engine activity.
 */
export type CheckMyAppResultResponse =
  | { status: "pending"; startedAt?: string; engines?: AsoEngineChildStatus[] }
  | { status: "failed"; error?: string }
  | { status: "completed"; auditId: string; startedAt?: string; engines?: AsoEngineChildStatus[] };

/**
 * Poll for a lead-audit result by claim token. Returns the polling status
 * directly (not wrapped in success/failure semantics — `failed` is a valid
 * terminal state, not a request-level error).
 *
 * Public route — token is the security credential.
 */
export async function apiPostCheckMyAppResult(
  request: { token: string },
): Promise<MarketingApiResponse<CheckMyAppResultResponse>> {
  return marketingRequest<CheckMyAppResultResponse>(
    "aso/check-my-app/result",
    "POST",
    null,
    { token: request.token },
  );
}

// ---------------------------------------------------------------------------
// Turnstile
// ---------------------------------------------------------------------------

export async function apiValidateTurnstileToken(
  turnstileToken: string,
): Promise<MarketingApiResponse> {
  return marketingRequest("org/turnstile-token", "POST", null, {
    turnstileToken,
  });
}

// ---------------------------------------------------------------------------
// Signup wizard
// ---------------------------------------------------------------------------

export async function apiCheckAppId(
  turnstileToken: string,
  storeId: string,
  appId: string,
  regionId?: string | null,
  languageId?: string | null,
): Promise<MarketingApiResponse> {
  const body: Record<string, string> = { storeId, appId };
  if (regionId) body.regionId = regionId;
  if (languageId) body.languageId = languageId;
  return marketingRequest("signup/appid-check", "POST", turnstileToken, body);
}

export async function apiValidateOneClick(
  turnstileToken: string,
  body: MarketingApiRequestBody,
): Promise<MarketingApiResponse> {
  return marketingRequest("signup/oneclick-check", "POST", turnstileToken, body);
}

export async function apiGetScreens(
  turnstileToken: string,
  appStoreId: string | number | null,
  playStoreId: string | number | null,
): Promise<MarketingApiResponse> {
  return marketingRequest("signup/screens", "POST", turnstileToken, {
    appStoreId: appStoreId?.toString() ?? null,
    playStoreId,
  });
}

export async function apiCreateTempAccount(
  turnstileToken: string,
  formData: MarketingApiRequestBody,
): Promise<MarketingApiResponse> {
  return marketingRequest(
    "signup/temp-account",
    "POST",
    turnstileToken,
    formData,
  );
}

export async function apiGetAudit(
  turnstileToken: string,
  appStoreId: string | null,
  playStoreId: string | null,
): Promise<MarketingApiResponse> {
  const queryParams: Record<string, string> = {};
  if (appStoreId) queryParams.appStoreId = appStoreId;
  if (playStoreId) queryParams.playStoreId = playStoreId;
  return marketingRequest(
    "signup/audit",
    "GET",
    turnstileToken,
    null,
    queryParams,
  );
}

export async function apiStartCheckout(
  turnstileToken: string,
  tempAccountId: string,
  planConfig: MarketingApiRequestBody,
  referralId?: string | null,
  couponCode?: string | null,
): Promise<MarketingApiResponse> {
  return marketingRequest(
    "signup/checkout-session-start",
    "POST",
    turnstileToken,
    { tempAccountId, planConfig, referralId, couponCode },
  );
}

export async function apiCompleteCheckout(
  turnstileToken: string,
  tempAccountId: string,
  checkoutSessionId: string,
  cancelled: boolean,
): Promise<MarketingApiResponse> {
  return marketingRequest(
    "signup/checkout-session-complete",
    "POST",
    turnstileToken,
    { tempAccountId, checkoutSessionId, cancelled },
  );
}


// ---------------------------------------------------------------------------
// Unsubscribe
// ---------------------------------------------------------------------------

export async function apiUnsubscribeFromProductEmails(
  email: string,
): Promise<MarketingApiResponse> {
  return marketingRequest("unsubscribe", "POST", null, { email });
}

// ---------------------------------------------------------------------------
// Email Preferences
// ---------------------------------------------------------------------------

/**
 * Get email preferences for a user identified by a signed token.
 * This is a root-level route (not product-scoped).
 */
export async function apiGetEmailPreferences(
  token: string,
): Promise<MarketingApiResponse<EmailPreferencesGetResponse>> {
  const url = buildNonScopedApiUrl("email-preferences", { token });
  const res = await fetch(url);

  if (!res.ok) {
    const errorBody = await res.json().catch(() => ({}));
    return {
      success: false,
      statusText: res.statusText,
      statusCode: res.status,
      error: errorBody,
    };
  }

  const data = (await res.json()) as EmailPreferencesGetResponse;
  return { success: true, statusCode: res.status, data };
}

/**
 * Update email preferences for a user identified by a signed token.
 * This is a root-level route (not product-scoped).
 */
export async function apiUpdateEmailPreferences(
  token: string,
  update: EmailPreferencesUpdateRequest,
): Promise<MarketingApiResponse<EmailPreferencesUpdateResponse>> {
  const url = buildNonScopedApiUrl("email-preferences", { token });
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(update),
  });

  if (!res.ok) {
    const errorBody = await res.json().catch(() => ({}));
    return {
      success: false,
      statusText: res.statusText,
      statusCode: res.status,
      error: errorBody,
    };
  }

  const data = (await res.json()) as EmailPreferencesUpdateResponse;
  return { success: true, statusCode: res.status, data };
}

/**
 * One-click global unsubscribe via a signed token.
 * This is a root-level route (not product-scoped).
 */
export async function apiGlobalUnsubscribe(
  token: string,
): Promise<MarketingApiResponse> {
  const url = buildNonScopedApiUrl("email-preferences/global-unsubscribe");
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token }),
  });

  if (!res.ok) {
    const errorBody = await res.json().catch(() => ({}));
    return {
      success: false,
      statusText: res.statusText,
      statusCode: res.status,
      error: errorBody,
    };
  }

  const data = await res.json();
  return { success: true, statusCode: res.status, data };
}

// ---------------------------------------------------------------------------
// Email Cadence (A7Y-3372)
// ---------------------------------------------------------------------------

/**
 * Apply (or undo, via a second call with the previous value) a one-click
 * audit-email cadence change from the `/email-cadence` confirmation page.
 * This is a root-level route (not product-scoped) — same trust pattern as
 * `apiGetEmailPreferences`/`apiUpdateEmailPreferences`: the token carries
 * the project/org identity, so no Clerk auth is required.
 */
export async function apiApplyEmailCadence(
  token: string,
  cadence: AuditEmailFrequency,
): Promise<MarketingApiResponse<EmailCadenceApplyResponse>> {
  const url = buildNonScopedApiUrl("email-cadence");
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, cadence }),
  });

  if (!res.ok) {
    const errorBody = await res.json().catch(() => ({}));
    return {
      success: false,
      statusText: res.statusText,
      statusCode: res.status,
      error: errorBody,
    };
  }

  const data = (await res.json()) as EmailCadenceApplyResponse;
  return { success: true, statusCode: res.status, data };
}

// ---------------------------------------------------------------------------
// Public Audit (AppCheck)
// ---------------------------------------------------------------------------

/**
 * Start a public audit engine run for the /appcheck page.
 * Uses Turnstile auth instead of Clerk JWT.
 */
export async function apiStartPublicAudit(
  turnstileToken: string,
  storeId: string,
  appIdentifier: string,
  countryCode?: string,
  languageCode?: string,
): Promise<MarketingApiResponse<{ instanceId: string; status: string }>> {
  return marketingRequest<{ instanceId: string; status: string }>(
    "appcheck/start",
    "POST",
    turnstileToken,
    {
      engineType: "audit",
      appIdentifier,
      storeId,
      countryCode: countryCode || "us",
      ...(languageCode ? { languageCode } : {}),
    },
  );
}

/**
 * Connect to SSE stream for a public audit run.
 * No auth needed — instanceId is the security token.
 */
export function connectPublicEngineSSE({
  instanceId,
  onOpen = () => {},
  onInit = () => {},
  onUpdate = () => {},
  onComplete = () => {},
  onPing = () => {},
  onError = () => {},
  onReconnect = () => {},
}: {
  instanceId: string;
  onOpen?: () => void;
  onInit?: (payload: AsoEngineSseInitEvent) => void;
  onUpdate?: (payload: AsoEngineSseUpdateEvent) => void;
  onComplete?: (payload: AsoEngineSseCompleteEvent) => void;
  onPing?: () => void;
  onError?: (error: unknown) => void;
  onReconnect?: () => void;
}) {
  const url = buildApiUrl(`appcheck/stream/${instanceId}`, null);
  if (typeof EventSource === "undefined") {
    throw new Error("EventSource is not available in this environment");
  }
  const es = openStreamEventSource(url);

  es.onopen = () => {
    try { onOpen(); } catch (err) { console.debug("Public SSE onOpen callback error", err); }
  };

  es.addEventListener("init", (e) => {
    try {
      const payload = JSON.parse(e.data);
      onInit(payload);
    } catch {
      onInit(e.data as unknown as AsoEngineSseInitEvent);
    }
  });

  es.addEventListener("update", (e) => {
    try {
      const payload = JSON.parse(e.data);
      onUpdate(payload);
    } catch (err) {
      console.warn("Public SSE update parse error:", err, e.data);
    }
  });

  es.addEventListener("complete", (e) => {
    try {
      const payload = JSON.parse(e.data);
      onComplete(payload);
    } catch (err) {
      console.warn("Public SSE complete parse error:", err, e.data);
    }
    es.close();
  });

  es.addEventListener("reconnect", () => {
    es.close();
    onReconnect();
  });

  es.addEventListener("ping", () => {
    try { onPing(); } catch { /* ignore */ }
  });

  es.onerror = (e) => {
    if (es.readyState === EventSource.CLOSED) return;
    onError(e);
  };

  return () => {
    es.close();
  };
}

// ---------------------------------------------------------------------------
// Release Notes
// ---------------------------------------------------------------------------

/** Shape of the release notes API response payload. */
export type ReleaseNotesApiData = ReleaseNotesResponse;

/**
 * Fetch public release notes. This endpoint is NOT product-scoped,
 * so it uses `buildNonScopedApiUrl` to avoid the /:productId prefix.
 */
export async function apiFetchReleaseNotes(): Promise<MarketingApiResponse<ReleaseNotesApiData>> {
  const url = buildNonScopedApiUrl("release-notes");
  const res = await fetch(url);

  if (!res.ok) {
    const errorBody = await res.json().catch(() => ({}));
    return {
      success: false,
      statusText: res.statusText,
      statusCode: res.status,
      error: errorBody,
    };
  }

  const data = (await res.json()) as ReleaseNotesApiData;
  return { success: true, statusCode: res.status, data };
}

// ---------------------------------------------------------------------------
// Public marketing stats (A7Y-4498)
// ---------------------------------------------------------------------------

/**
 * Fetch the public keyword-count marketing stat. Public route — no Clerk JWT,
 * no Turnstile. A 503 (`{ source: 'unavailable' }`, no live or cached value)
 * comes back as `success: false` via `marketingRequest`'s `!res.ok` branch,
 * same as every other non-2xx response on this client.
 */
export async function apiFetchPublicKeywordCount(): Promise<
  MarketingApiResponse<PublicKeywordCountResponse>
> {
  return marketingRequest<PublicKeywordCountResponse>("public-stats/keyword-count", "GET", null, null);
}

/**
 * Fetch the public platform counts: the latest Weekly Bulletin metrics
 * snapshot (A7Y-5608), stored weekly in Firestore and served from the API's
 * cache. A 503 comes back as `success: false`.
 */
export async function apiFetchPublicPlatformCounts(): Promise<
  MarketingApiResponse<PublicPlatformCountsResponse>
> {
  return marketingRequest<PublicPlatformCountsResponse>("public-stats/platform-counts", "GET", null, null);
}

// ---------------------------------------------------------------------------
// Weekly Bulletin signup (A7Y-6115)
// ---------------------------------------------------------------------------

/**
 * Subscribe an address to The Weekly Bulletin. Public route: the Turnstile
 * token is the credential, sent as the bearer. The response is the same for
 * every address, so it says only that a confirmation email may be on its way.
 */
export async function apiNewsletterSubscribe(
  request: NewsletterSubscribeRequest & { turnstileToken: string | null },
): Promise<MarketingApiResponse<NewsletterSubscribeResponse>> {
  const { turnstileToken, ...body } = request;
  return marketingRequest<NewsletterSubscribeResponse>(
    "newsletter/subscribe",
    "POST",
    turnstileToken,
    body as MarketingApiRequestBody,
  );
}

/** Redeem the single-use token from the confirmation email. */
export async function apiNewsletterConfirm(
  request: NewsletterConfirmRequest,
): Promise<MarketingApiResponse<NewsletterConfirmResponse>> {
  return marketingRequest<NewsletterConfirmResponse>(
    "newsletter/confirm",
    "POST",
    null,
    request as unknown as MarketingApiRequestBody,
  );
}
